import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';
import Resources from '../../helpers/resources';

// Components
import Button, { BUTTON_STYLES } from '../button/button';
import Form from '../form/form';
import ThreeEssentailsButton from '../three-essentails-button/three-essentails-button';
import GenericModal, { MODAL_TYPE } from '../generic-modal/generic-modal';

// Helpers
import { CONFIG_VARS } from '../../helpers/enums';
import { getCorrectErrorMessage } from '../../helpers/utils';
import useIsOneRosterActivatedSchool from '../../hooks/use-is-one-roster-activated-school';

function StudentPassword({ id, userId, password, changePassword, shownPassword, setShownPassword, oneRosterUserId }) {
    const { i18n } = useLocalisation();
    const defaultPasswordText = '********';
    const isOneRosterActivatedSchool = useIsOneRosterActivatedSchool();

    // States
    const [showText, setShowText] = useState(false);
    const [showEditButton, setShowEditButton] = useState(true);
    const [updatedPassword, setUpdatedPassword] = useState(password);
    const [disableSaveButton, setDisableSaveButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Handler
    const showPasswordHandler = () => {
        setShowText(prevShowText => !prevShowText);
        setShowEditButton(true);
        setShownPassword(showText ? '' : id);
    };
    // This is to reset showText when password is hidden through another line. BA 2020-04-16
    useEffect(() => {
        setShowText(shownPassword === id);
    }, [shownPassword]);

    const saveHandler = async (e) => {
        // Prevent redirect from the default behaviour of a form
        e.preventDefault();
        const newPassword = updatedPassword.trim();

        const value = await changePassword({
            userProfileId: userId,
            newPassword
        });

        if (value === true) {
            setUpdatedPassword(newPassword);
            setShowEditButton(prevShowEditButton => !prevShowEditButton);
        } else if (value && value.message) {
            setErrorMessage(getCorrectErrorMessage(value, i18n));
        } else {
            // Covering generic, unexpected error
            setErrorMessage('error');
        }
    };

    const showEditButtonHandler = async (e) => {
        if (showEditButton) {
            setShowEditButton(prevShowEditButton => !prevShowEditButton);
            setUpdatedPassword(password);
        } else {
            await saveHandler(e);
        }
    };

    const passwordOnChangeHandler = (e) => {
        const myInput = e.target;
        const myValue = myInput.value;
        setUpdatedPassword(myValue);
        setDisableSaveButton(myValue.length === 0);
    };

    const closeErrorModal = () => {
        setErrorMessage('');
    };

    const onEditPasswordClickHandler = async () => {
        await Resources.getConfigurationItem('THREE_ESSENTIALS_MANAGE_STUDENTS').then((manageStudentsUrl) => {
            window.open(`${manageStudentsUrl}/uid/${oneRosterUserId}/edit`);
        });
    };

    // Components
    const passwordHidden = (
        <Fragment>
            {defaultPasswordText}
            <div className="table__buttons-container">
                <Button
                    buttonStyle={BUTTON_STYLES.iconBackground}
                    importedClasses="button--password button--eye-hidden"
                    callback={showPasswordHandler}
                    ariaProps={
                        {
                            role: 'switch',
                            'aria-label': i18n._(t`Password - Show password`),
                            'aria-checked': showText
                        }
                    } />
            </div>
        </Fragment>
    );

    const buttonEyeVisible = (
        <Button
            buttonStyle={BUTTON_STYLES.iconBackground}
            importedClasses="button--password button--eye-visible"
            callback={showPasswordHandler}
            ariaProps={
                {
                    role: 'switch',
                    'aria-label': i18n._(t`Password - Show password`),
                    'aria-checked': showText
                }
            } />
    );

    const passwordReadable = (
        <Fragment>
            {password}
            <div className="table__buttons-container table__buttons-container--center">
                {!isOneRosterActivatedSchool ? (
                    <Button
                        buttonStyle={BUTTON_STYLES.iconBackground}
                        importedClasses="button--password button--edit"
                        isBtnDisabled={!showEditButton && disableSaveButton}
                        callback={showEditButtonHandler}
                        buttonType="button"
                        ariaProps={
                            {
                                'aria-label': i18n._(t`Password - Edit password`)
                            }
                        } />
                ) : (
                    <ThreeEssentailsButton
                        clickHandler={onEditPasswordClickHandler}
                        title={i18n._(t`Password - Edit`)}
                        show3EIcon={false}
                        buttonClassName={'edit_password'}
                    />
                )}
                {buttonEyeVisible}
            </div>
        </Fragment>
    );

    const passwordEditable = (
        <Fragment>
            <Form formHandler={saveHandler} parentClasses="form--naked" id={`form-password-${id}`}>
                <label htmlFor={`password-${id}`}>
                    <span className="sr-only">
                        <Trans id={'Password - Password to edit'} />{id}
                    </span>
                </label>
                { isOneRosterActivatedSchool ?
                    <label htmlFor={`password-${id}`}>
                        <span className="sr-only">
                            {updatedPassword}
                        </span>
                    </label>
                    :
                    <input
                        type="text"
                        id={`password-${id}`}
                        value={updatedPassword}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        className="form__input form__input--password"
                        maxLength={CONFIG_VARS.passwordMaxLength}
                        onChange={passwordOnChangeHandler}
                    />
                }
            </Form>
            <div className="table__buttons-container">
                <Button
                    buttonStyle={BUTTON_STYLES.iconBackground}
                    importedClasses="button--password button--tick"
                    isBtnDisabled={!showEditButton && disableSaveButton}
                    callback={showEditButtonHandler}
                    buttonType="submit"
                    ariaProps={
                        {
                            'aria-label': i18n._(t`Password - Save password`)
                        }
                    } />
                {buttonEyeVisible}
            </div>
        </Fragment>
    );

    const passwordVisible = showEditButton ? passwordReadable : passwordEditable;

    return (
        <Fragment>
            {showText && (shownPassword === '' || shownPassword === id) ? passwordVisible : passwordHidden}
            {errorMessage !== '' &&
                <GenericModal
                    title={<Trans id="Error Message - Title" />}
                    type={MODAL_TYPE.floating}
                    closeHandler={closeErrorModal}>
                    <p className="modal__text modal__text--error">
                        <Trans id={'Error Message - Unable to proceed'} />
                    </p>
                    <div className="form__row form__row--buttons">
                        <Button
                            buttonStyle={BUTTON_STYLES.text}
                            callback={closeErrorModal}
                            visibleText={<Trans id={'Button - Ok'} />} />
                    </div>
                </GenericModal>
            }
        </Fragment>
    );
}

StudentPassword.propTypes = {
    id: PropTypes.string,
    userId: PropTypes.number.isRequired,
    password: PropTypes.string,
    changePassword: PropTypes.func.isRequired,
    shownPassword: PropTypes.string,
    setShownPassword: PropTypes.func,
    oneRosterUserId: PropTypes.string
};

export default StudentPassword;



// WEBPACK FOOTER //
// ./components/student-password/student-password.js